// Unlock an existing vault in a browser that doesn't hold the key yet.
//
// This is the screen someone lands on the first time they open Oreia on their laptop after using
// the iPhone app. Everything they've written is already on the server as ciphertext; the only thing
// missing is the key, and only their password or their recovery phrase can produce it.

import { useState, type FormEvent } from 'react';
import { useSession } from '../auth/SessionProvider';
import { normalizeRecoveryPhrase, unknownRecoveryWords, RECOVERY_WORD_COUNT } from '../crypto/vault';
import { Notice, PrimaryButton } from '../components/ui';
import { IconKey, IconLock } from '../components/Icons';

export function VaultUnlockScreen() {
  const { vault, unlockWithPassword, unlockWithRecoveryPhrase, signOut, user } = useSession();

  const hasPasswordWrap = vault.status === 'locked' ? vault.hasPasswordWrap : true;
  const hasRecoveryWrap = vault.status === 'locked' ? vault.hasRecoveryWrap : true;

  const [mode, setMode] = useState<'password' | 'recovery'>(
    hasPasswordWrap ? 'password' : 'recovery',
  );
  const [password, setPassword] = useState('');
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (mode === 'recovery') {
      // Say precisely what's wrong before spending 100k PBKDF2 iterations to fail generically.
      const unknown = unknownRecoveryWords(phrase);
      if (unknown.length > 0) {
        setError(
          unknown.length === 1
            ? `"${unknown[0]}" isn't one of the recovery words. Check the spelling.`
            : `These aren't recovery words: ${unknown.join(', ')}.`,
        );
        return;
      }
      if (!normalizeRecoveryPhrase(phrase)) {
        setError(`A recovery phrase is exactly ${RECOVERY_WORD_COUNT} words.`);
        return;
      }
    }

    setBusy(true);
    try {
      const opened =
        mode === 'password' ? await unlockWithPassword(password) : await unlockWithRecoveryPhrase(phrase);

      if (!opened) {
        setError(
          mode === 'password'
            ? "That password didn't unlock your journal. If you changed it recently, use your recovery phrase."
            : "That phrase didn't unlock your journal. Check the word order.",
        );
      }
    } catch {
      setError('Could not reach your vault. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="oscreen">
      <div className="oscroll">
        <div className="ocolumn opane">
          <div className="orow" style={{ gap: 10, marginBottom: 18, color: 'var(--accent-1)' }}>
            <IconLock style={{ width: 20, height: 20 }} />
            <span className="olabel" style={{ color: 'var(--accent-1)' }}>Locked</span>
          </div>

          <h1 className="oserif" style={{ fontSize: 40, margin: '0 0 12px' }}>
            Unlock your journal.
          </h1>
          <p className="otext-soft" style={{ fontSize: 15, lineHeight: 1.65, margin: '0 0 26px' }}>
            Your entries are here, encrypted. This browser doesn't have your key yet — it never
            leaves your devices, so we can't hand it over for you.
          </p>

          <form className="ostack" style={{ gap: 16 }} onSubmit={onSubmit} noValidate>
            {mode === 'password' ? (
              <div className="ostack" style={{ gap: 7 }}>
                <label className="olabel" htmlFor="unlock-password">
                  Account password
                </label>
                <input
                  id="unlock-password"
                  className="oinput"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your Oreia password"
                />
              </div>
            ) : (
              <div className="ostack" style={{ gap: 7 }}>
                <label className="olabel" htmlFor="unlock-phrase">
                  Recovery phrase
                </label>
                <textarea
                  id="unlock-phrase"
                  className="oinput mono"
                  rows={3}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder="eight words, in order"
                />
                <span className="otext-mute" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                  Spaces, hyphens or commas all work.
                </span>
              </div>
            )}

            {error ? <Notice tone="danger">{error}</Notice> : null}

            <PrimaryButton type="submit" loading={busy}>
              <IconKey /> Unlock
            </PrimaryButton>
          </form>

          <div className="ostack" style={{ gap: 12, marginTop: 24, fontSize: 13.5 }}>
            {mode === 'password' && hasRecoveryWrap ? (
              <button className="olink-plain" type="button" onClick={() => { setMode('recovery'); setError(null); }}>
                Forgot your password? <strong>Use your recovery phrase</strong>
              </button>
            ) : null}
            {mode === 'recovery' && hasPasswordWrap ? (
              <button className="olink-plain" type="button" onClick={() => { setMode('password'); setError(null); }}>
                <strong>Use your password instead</strong>
              </button>
            ) : null}
            <button className="olink-plain" type="button" onClick={signOut}>
              Sign out{user?.email ? ` of ${user.email}` : ''}
            </button>
          </div>

          <div style={{ marginTop: 28 }}>
            <Notice>
              Lost both your password and your recovery phrase? Then your existing entries cannot be
              decrypted by anyone, including us — that is the trade we made for real privacy. Get in
              touch and we can start you a fresh vault.
            </Notice>
          </div>
        </div>
      </div>
    </div>
  );
}
